// client/src/pages/seo/OnlineTischreservierungDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Braucht Ihr Restaurant Online-Tischreservierung?",
  },
  {
    id: "definition",
    label: "Was ist Online-Tischreservierung?",
  },
  {
    id: "phone",
    label: "Warum telefonische Reservierungen nicht immer ausreichen",
  },
  {
    id: "always-on",
    label: "Online-Reservierung funktioniert auch außerhalb der Öffnungszeiten",
  },
  {
    id: "menu",
    label: "Wie Online-Reservierung mit einer digitalen Speisekarte zusammenhängt",
  },
  {
    id: "planning",
    label: "Wie Online-Reservierung bei der Planung hilft",
  },
  {
    id: "no-show",
    label: "No-Shows: Was Online-Reservierung lösen kann und was nicht",
  },
  {
    id: "for-whom",
    label: "Welche Restaurants profitieren besonders von Online-Reservierung?",
  },
  {
    id: "mistakes",
    label: "Häufige Fehler bei Online-Reservierungen",
  },
  {
    id: "bestplace",
    label: "Wie Best Place bei Online-Reservierung helfen kann",
  },
  {
    id: "faq",
    label: "FAQ zur Online-Tischreservierung",
  },
];

const checklist = [
  "Gäste rufen häufig während der Stoßzeiten an.",
  "Ihr Team kann das Telefon nicht immer sofort beantworten.",
  "Reservierungen kommen über Google, Instagram, Ihre Website oder Nachrichten.",
  "Sie möchten Reservierungsanfragen auch außerhalb der Öffnungszeiten annehmen.",
  "Sie möchten Tische und Gäste im Voraus klarer planen.",
  "Sie möchten weniger Chaos mit Notizen, Anrufen oder Nachrichten.",
  "Datum, Uhrzeit, Personenzahl und Kontaktdaten sollen an einem Ort sein.",
  "Reservierung sollte mit Ihrer Speisekarte oder Restaurantseite verbunden sein.",
  "Ihr Restaurant soll für jüngere Gäste und Touristen einfacher wirken.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Online-Reservierung ist vielleicht noch nicht dringend",
      text:
        "Telefon oder eine einfache Kontaktmöglichkeit können noch ausreichen. Ein kleiner Online-Reservierungsablauf kann aber helfen, Anfragen strukturierter zu sammeln.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Ein Test ist sinnvoll",
      text:
        "Online-Tischreservierung kann Ihrem Restaurant helfen, Telefonstress zu reduzieren, Reservierungsdetails klarer zu erfassen und Gästen einen bequemeren Weg zu geben.",
      tone: "medium",
    };
  }

  return {
    title: "Online-Reservierung kann ein wichtiger Teil Ihres Systems werden",
    text:
      "Mehrere Punkte sprechen dafür, dass digitale Reservierungen Ihrem Restaurant helfen können: bessere Planung, weniger verpasste Anfragen, ein kürzerer Weg von der Speisekarte zur Buchung und einfacherer Zugang für Gäste.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function OnlineTischreservierungDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Online-Tischreservierung für Restaurants | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/online-table-reservation-for-restaurants",
          de: "/de/online-tischreservierung",
          no: "/no/online-bordreservasjon-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Online-Tischreservierung für Restaurants: weniger Telefonstress,
              besser planbare Gäste
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Online-Reservierung ersetzt keine Gastfreundschaft. Sie entfernt
                unnötige Reibung, bevor der Gast überhaupt im Restaurant
                ankommt.
              </p>

              <p className="seoHero__text">
                Wenn ein Gast Ihre Speisekarte gesehen, Ihr Restaurant bei
                Google gefunden oder Ihre Seite über einen QR-Code geöffnet hat,
                ist die nächste Frage einfach: Kann ich jetzt einen Tisch
                reservieren?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Braucht Ihr Restaurant Online-Tischreservierung?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Nicht jedes Restaurant braucht sofort
              Online-Reservierung. Viele Restaurants profitieren aber früher als
              gedacht von einem einfachen Buchungsablauf.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Was ist Online-Tischreservierung?</h2>

          <p>
            Online-Tischreservierung bedeutet, dass Gäste über eine
            Restaurant-Website oder eine Online-Restaurantseite Datum, Uhrzeit,
            Personenzahl und Kontaktdaten auswählen beziehungsweise angeben
            können.
          </p>

          <p>
            Für Gäste ist das bequem, weil sie nicht anrufen, warten oder
            Nachrichten über soziale Medien senden müssen. Für Restaurants
            entsteht ein klarerer Prozess: Reservierungen verteilen sich nicht
            mehr auf Telefonanrufe, Notizzettel, Instagram-Nachrichten und
            Erinnerung.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Datum.</li>
            <li className="text__seo-li">Uhrzeit.</li>
            <li className="text__seo-li">Personenzahl.</li>
            <li className="text__seo-li">Name.</li>
            <li className="text__seo-li">Telefon oder E-Mail.</li>
            <li className="text__seo-li">Kommentar.</li>
            <li className="text__seo-li">Reservierungsstatus.</li>
          </ul>

          <h3>Reservierungen sollten strukturiert sein, nicht verstreut</h3>

          <p>
            Der Hauptvorteil liegt nicht nur darin, dass der Gast eine Anfrage
            online senden kann. Das Restaurant erhält die wichtigen Informationen
            auch in einem klareren Format.
          </p>
        </section>

        <section id="phone" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum telefonische Reservierungen nicht immer ausreichen</h2>

            <p>
              Das Telefon bleibt wichtig. Es hat aber eine Schwäche: Es
              funktioniert nur dann gut, wenn jemand sofort Zeit hat, den Anruf
              anzunehmen.
            </p>

            <h3>Ein verpasster Anruf kann ein verpasster Tisch werden</h3>

            <p>
              Während der Stoßzeiten ist das Team vielleicht im Service, Gäste
              warten, die Küche ist beschäftigt und das Telefon klingelt genau
              im ungünstigsten Moment.
            </p>

            <p>
              Online-Reservierung ersetzt das Telefon nicht. Sie ergänzt einen
              zweiten Kanal, der parallel funktionieren kann.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Nur Telefon</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Jemand muss sofort antworten.
                  </li>
                  <li className="text__seo-li">
                    Anrufe unterbrechen den Service.
                  </li>
                  <li className="text__seo-li">
                    Details können falsch notiert werden.
                  </li>
                  <li className="text__seo-li">
                    Anfragen außerhalb der Öffnungszeiten können verloren gehen.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Telefon + Online-Anfrage</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Gäste können weiterhin anrufen, wenn sie möchten.
                  </li>
                  <li className="text__seo-li">
                    Online-Anfragen erfassen die wichtigsten Details.
                  </li>
                  <li className="text__seo-li">
                    Das Team kann Anfragen später prüfen.
                  </li>
                  <li className="text__seo-li">
                    Weniger Reservierungen gehen in hektischen Momenten
                    verloren.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="always-on" className="acquaintance">
          <h2>
            Online-Reservierung funktioniert auch außerhalb der Öffnungszeiten
          </h2>

          <p>
            Gäste planen Restaurantbesuche nicht immer während Ihrer
            Öffnungszeiten. Sie entscheiden sich vielleicht spät am Abend, am
            Wochenende, unterwegs auf Reisen oder direkt nachdem sie Ihre
            Speisekarte gelesen haben.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>23:14:</strong> Gast sieht Ihre Speisekarte auf dem
              Smartphone.
            </li>

            <li className="text__seo-li">
              <strong>23:16:</strong> Gast wählt Datum, Uhrzeit und
              Personenzahl.
            </li>

            <li className="text__seo-li">
              <strong>23:17:</strong> Reservierungsanfrage wird gesendet.
            </li>

            <li className="text__seo-li">
              <strong>Morgens:</strong> Ihr Restaurant sieht die neue Anfrage.
            </li>
          </ol>

          <p>
            Das ist der praktische Wert: Online-Reservierung funktioniert weiter,
            wenn Ihr Team telefonisch nicht erreichbar ist.
          </p>

          <VisualIdea>
            Ein einfacher Abendablauf: Ein Gast öffnet nachts die Speisekarte,
            klickt auf Reservieren, sendet eine Anfrage und das Restaurant sieht
            sie am Morgen.
          </VisualIdea>
        </section>

        <section id="menu" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Wie Online-Reservierung mit einer digitalen Speisekarte
              zusammenhängt
            </h2>

            <p>
              Digitale Speisekarte und Online-Reservierung sollten nicht als
              getrennte Ideen betrachtet werden. Oft prüft der Gast zuerst die
              Speisekarte, Preise, Bilder und Angebote — und entscheidet dann,
              ob er das Restaurant besuchen möchte.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">Speisekarte ansehen.</li>
              <li className="text__seo-li">Gerichte und Preise prüfen.</li>
              <li className="text__seo-li">Interesse entsteht.</li>
              <li className="text__seo-li">Tisch reservieren.</li>
            </ol>

            <p>
              Wenn nach der Speisekarte ein klarer Button „Tisch reservieren“
              erscheint, wird der Weg kürzer. Die Speisekarte beantwortet: Was
              bekomme ich hier? Die Reservierung beantwortet: Wie kann ich
              besuchen?
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/digitale-speisekarte">
                    Digitale Speisekarte für Restaurants
                  </Link>
                </p>
              </div>

              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/qr-code-speisekarte">
                    QR-Code-Speisekarte für Restaurants
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="planning" className="acquaintance">
          <h2>Wie Online-Reservierung bei der Planung hilft</h2>

          <p>
            Wenn Reservierungen über viele verschiedene Kanäle kommen, entsteht
            schnell Chaos. Online-Reservierung hilft, wichtige Informationen in
            einem einheitlichen Format zu sammeln.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Wann kommt der Gast?</li>
            <li className="text__seo-li">Wie viele Personen?</li>
            <li className="text__seo-li">Gibt es einen Kommentar?</li>
            <li className="text__seo-li">Ist die Reservierung bestätigt?</li>
            <li className="text__seo-li">Gibt es besondere Hinweise?</li>
            <li className="text__seo-li">Braucht jemand einen Rückruf?</li>
          </ul>

          <p>
            Je besser ein Restaurant seine Reservierungen im Blick hat, desto
            besser kann es den Abend planen: Tische, Personal, Küchenbelastung,
            Stoßzeiten und größere Gruppen.
          </p>
        </section>

        <section id="no-show" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              No-Shows: Was Online-Reservierung lösen kann und was nicht
            </h2>

            <p>
              Online-Reservierung ist keine Garantie gegen No-Shows. Gäste
              können trotzdem nicht erscheinen. Aber sie kann den Prozess klarer
              und strukturierter machen.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Kontaktdaten werden klar erfasst.
              </li>
              <li className="text__seo-li">
                Bestätigungen sind leichter verständlich.
              </li>
              <li className="text__seo-li">
                Stornierungen können einfacher werden.
              </li>
              <li className="text__seo-li">
                Kommentare und Sonderwünsche bleiben dokumentiert.
              </li>
              <li className="text__seo-li">
                Das Team sieht Reservierungen an einem Ort.
              </li>
            </ul>

            <p>
              Der ehrliche Punkt ist: Digitale Reservierung löst nicht jedes
              Problem. Sie kann Restaurants aber helfen, Reservierungsdetails
              besser zu erfassen und Entscheidungen im täglichen Service
              einfacher zu machen.
            </p>
          </div>
        </section>

        <section id="for-whom" className="acquaintance">
          <h2>
            Welche Restaurants profitieren besonders von Online-Reservierung?
          </h2>

          <p>
            Online-Reservierung ist nicht für jedes Konzept gleich wichtig. Sie
            ist besonders nützlich dort, wo Tischplanung, Stoßzeiten oder
            Gruppenbuchungen eine Rolle spielen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Besonders nützlich für</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Restaurants mit Abendspitzen.
                </li>
                <li className="text__seo-li">Familienrestaurants.</li>
                <li className="text__seo-li">Pizzerien mit Sitzplätzen.</li>
                <li className="text__seo-li">Brunch-Cafés.</li>
                <li className="text__seo-li">Touristische Restaurants.</li>
                <li className="text__seo-li">Hotelrestaurants.</li>
                <li className="text__seo-li">Restaurants mit Terrasse.</li>
                <li className="text__seo-li">Gruppenreservierungen.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Weniger entscheidend für</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Reine Takeaway-Konzepte.</li>
                <li className="text__seo-li">Foodtrucks.</li>
                <li className="text__seo-li">
                  Sehr kleine Cafés mit nur wenigen Tischen.
                </li>
                <li className="text__seo-li">
                  Betriebe ohne Sitzplätze.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Häufige Fehler bei Online-Reservierungen</h2>

            <p>
              Online-Reservierung hilft nur, wenn der Ablauf einfach, klar und
              mit dem echten Restaurantbetrieb verbunden ist.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Das Formular fragt zu viele Informationen ab.
                  </li>
                  <li className="text__seo-li">
                    Gäste wissen nicht, ob die Reservierung bestätigt ist.
                  </li>
                  <li className="text__seo-li">
                    Die mobile Erfahrung ist schwach.
                  </li>
                  <li className="text__seo-li">
                    Der Reservierungsbutton ist versteckt.
                  </li>
                  <li className="text__seo-li">
                    Das Team sieht Reservierungen zu spät.
                  </li>
                  <li className="text__seo-li">
                    Es gibt keine Telefonalternative für Gäste, die lieber
                    anrufen.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Das Formular kurz und klar halten.
                  </li>
                  <li className="text__seo-li">
                    Erklären, ob es eine Anfrage oder eine bestätigte Buchung
                    ist.
                  </li>
                  <li className="text__seo-li">
                    Das Formular auf Smartphones gut nutzbar machen.
                  </li>
                  <li className="text__seo-li">
                    Den Reservierungsbutton nach der Speisekarte platzieren.
                  </li>
                  <li className="text__seo-li">
                    Das Restaurant klar benachrichtigen.
                  </li>
                  <li className="text__seo-li">
                    Telefon und Online-Reservierung gemeinsam nutzen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zur Online-Tischreservierung</h2>

          <div className="seoFaq">
            <details>
              <summary>
                Braucht jedes Restaurant Online-Reservierung?
              </summary>
              <p>
                Nein. Für reine Takeaway-Konzepte oder sehr kleine Betriebe ist
                sie möglicherweise weniger wichtig. Für Restaurants mit
                Sitzplätzen, Stoßzeiten oder Gruppenreservierungen ist ein Test
                sinnvoller.
              </p>
            </details>

            <details>
              <summary>Ersetzt Online-Reservierung das Telefon?</summary>
              <p>
                Nicht unbedingt. Sie ergänzt das Telefon oft und gibt Gästen
                einen zweiten, bequemeren Kanal.
              </p>
            </details>

            <details>
              <summary>
                Können Gäste außerhalb der Öffnungszeiten reservieren?
              </summary>
              <p>
                Ja. Das ist einer der wichtigsten Vorteile: Gäste können eine
                Anfrage senden, auch wenn telefonisch niemand erreichbar ist.
              </p>
            </details>

            <details>
              <summary>Kann Online-Reservierung No-Shows verhindern?</summary>
              <p>
                Sie kann No-Shows nicht vollständig verhindern. Sie kann aber
                helfen, Kontaktdaten und Reservierungsinformationen klarer zu
                erfassen.
              </p>
            </details>

            <details>
              <summary>
                Kann Reservierung mit einer digitalen Speisekarte verbunden
                werden?
              </summary>
              <p>
                Ja. Das ist ein sehr natürlicher Weg: Der Gast prüft die
                Speisekarte und kann danach direkt einen Tisch reservieren.
              </p>
            </details>

            <details>
              <summary>Braucht der Gast eine App?</summary>
              <p>
                Nein. Die beste Erfahrung ist meistens ein browserbasierter
                Reservierungsablauf auf dem Smartphone.
              </p>
            </details>
          </div>
        </section>
        <section className="articls qrArticle__related">
                  <h2>Additional topics about digital menus and reservations</h2>
        
                  <div className="seoCompareSmall">
                    <p>
                      <Link to="/de/digitale-getraenkekarte-bar">
                        Digitale Getränkekarte für Bars
                      </Link>
                    </p>
                                    
                    <p>
                      <Link to="/de/restaurant-website">
                        Restaurant-Website, Reservierungen und Vertrauen an einem Ort.
                      </Link>
                    </p>
        
                    <p>
                      <Link to="/de/digitales-bestellsystem-gastronomie">
                        Digitales Bestellsystem für Restaurants
                      </Link>
                    </p>
        
                    <p>
                      <Link to="/de/restaurant-digitalisierung">
                        Der schrittweise Übergang von Restaurants zu digitalen Technologien
                      </Link>
                    </p>

                    <p>
                      <Link to="/de/digitale-speisekarte-vergleich">
                        Vergleich der Anbieter digitaler Speisekarten
                      </Link>
                    </p>
                  </div>
                </section>
      </article>
    </main>
  );
}